import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Calendar,
  Plus,
  Instagram,
  Facebook,
  Image,
  Video,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Upload,
  X,
  Clock,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Hash,
  TrendingUp,
  Zap,
  Loader2,
  Save,
  XCircle,
  FileText,
  Info,
} from "lucide-react";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function SocialMediaPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("calendar");
  const [posts, setPosts] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [showMediaDialog, setShowMediaDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [mediaFilter, setMediaFilter] = useState("all");
  const [visibilityFilter, setVisibilityFilter] = useState("all");

  // Form states
  const [postForm, setPostForm] = useState({
    title: "",
    content: "",
    scheduled_date: "",
    scheduled_time: "",
    platforms: [],
    media_ids: [],
    event_id: "",
    post_status: "draft"
  });

  const [uploadingMedia, setUploadingMedia] = useState(false);

  // AI States
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiContext, setAiContext] = useState(""); // Additional context for AI
  const [aiPlatform, setAiPlatform] = useState("all");
  const [aiTone, setAiTone] = useState("decontracte");
  const [aiResult, setAiResult] = useState(null);
  const [aiEventId, setAiEventId] = useState("none"); // Link to event for AI context
  const [showTrendsDialog, setShowTrendsDialog] = useState(false);
  const [trendsData, setTrendsData] = useState(null);
  const [showBestTimeDialog, setShowBestTimeDialog] = useState(false);
  const [bestTimeData, setBestTimeData] = useState(null);
  
  // Inline editing states
  const [editingInlineId, setEditingInlineId] = useState(null);
  const [inlineContent, setInlineContent] = useState("");
  const [savingInline, setSavingInline] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [postsRes, mediaRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/social/posts`, { headers }),
        axios.get(`${API_URL}/api/media`, { headers }),
        axios.get(`${API_URL}/api/media/events`, { headers })
      ]);

      setPosts(postsRes.data);
      setMediaItems(mediaRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // AI Functions
  const handleGenerateContent = async () => {
    if (!aiTopic.trim()) {
      toast.error("Veuillez entrer un sujet");
      return;
    }
    
    setAiLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/social/ai/generate-content`,
        {
          topic: aiTopic,
          platform: aiPlatform,
          tone: aiTone,
          include_hashtags: true,
          event_id: aiEventId !== "none" ? aiEventId : null,
          additional_context: aiContext || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAiResult(response.data.content);
      toast.success("Contenu généré avec succès !");
    } catch (error) {
      console.error("AI Error:", error);
      toast.error("Erreur lors de la génération");
    } finally {
      setAiLoading(false);
    }
  };

  const handleGetTrends = async () => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/social/ai/analyze-trends`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTrendsData(response.data.data);
      setShowTrendsDialog(true);
    } catch (error) {
      toast.error("Erreur lors de l'analyse des tendances");
    } finally {
      setAiLoading(false);
    }
  };

  const handleGetBestTime = async () => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/social/ai/best-time`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBestTimeData(response.data.data);
      setShowBestTimeDialog(true);
    } catch (error) {
      toast.error("Erreur lors de l'analyse");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAIContent = (platform, content) => {
    const text = content?.text || "";
    const hashtags = content?.hashtags?.join(" ") || "";
    setPostForm(prev => ({
      ...prev,
      content: text + (hashtags ? "\n\n" + hashtags : ""),
      platforms: platform === "all" ? ["instagram", "facebook", "tiktok"] : [platform]
    }));
    setShowAIDialog(false);
    setShowPostDialog(true);
    toast.success("Contenu appliqué !");
  };

  const handleCreatePost = async () => {
    try {
      const token = localStorage.getItem("token");
      
      if (editingPost) {
        await axios.put(
          `${API_URL}/api/social/posts/${editingPost.id}`,
          postForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Publication mise à jour");
      } else {
        await axios.post(
          `${API_URL}/api/social/posts`,
          postForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Publication créée");
      }

      setShowPostDialog(false);
      resetPostForm();
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Supprimer cette publication ?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/social/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Publication supprimée");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Inline editing functions
  const startInlineEdit = (post) => {
    setEditingInlineId(post.id);
    setInlineContent(post.content || "");
  };

  const cancelInlineEdit = () => {
    setEditingInlineId(null);
    setInlineContent("");
  };

  const saveInlineEdit = async (postId) => {
    setSavingInline(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/social/posts/${postId}`,
        { content: inlineContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Contenu mis à jour");
      setEditingInlineId(null);
      setInlineContent("");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingInline(false);
    }
  };

  const handleUploadMedia = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingMedia(true);
    const token = localStorage.getItem("token");

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("visibility", "private");

        await axios.post(`${API_URL}/api/media/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
      }

      toast.success(`${files.length} fichier(s) uploadé(s)`);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleUpdateMediaVisibility = async (mediaId, visibility) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/media/${mediaId}`,
        { visibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Visibilité mise à jour");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm("Supprimer ce média ?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Média supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetPostForm = () => {
    setPostForm({
      title: "",
      content: "",
      scheduled_date: "",
      scheduled_time: "",
      platforms: [],
      media_ids: [],
      event_id: "",
      post_status: "draft"
    });
    setEditingPost(null);
  };

  const openEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      title: post.title,
      content: post.content || "",
      scheduled_date: post.scheduled_date,
      scheduled_time: post.scheduled_time || "",
      platforms: post.platforms || [],
      media_ids: post.media_ids || [],
      event_id: post.event_id || "",
      post_status: post.status
    });
    setShowPostDialog(true);
  };

  const togglePlatform = (platform) => {
    setPostForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const toggleMediaSelection = (mediaId) => {
    setPostForm(prev => ({
      ...prev,
      media_ids: prev.media_ids.includes(mediaId)
        ? prev.media_ids.filter(id => id !== mediaId)
        : [...prev.media_ids, mediaId]
    }));
  };

  // Calendar helpers
  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const getPostsForDay = (date) => {
    return posts.filter(post => {
      const postDate = parseISO(post.scheduled_date);
      return isSameDay(postDate, date);
    });
  };

  const filteredMedia = mediaItems.filter(item => {
    if (mediaFilter !== "all" && item.media_type !== mediaFilter) return false;
    if (visibilityFilter !== "all" && item.visibility !== visibilityFilter) return false;
    return true;
  });

  const statusColors = {
    draft: "bg-gray-500",
    scheduled: "bg-blue-500",
    published: "bg-green-500",
    failed: "bg-red-500"
  };

  const statusLabels = {
    draft: "Brouillon",
    scheduled: "Planifié",
    published: "Publié",
    failed: "Échec"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-media-page">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-2xl font-heading font-bold text-secondary">
          Réseaux Sociaux & Médias
        </h1>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowAIDialog(true)}
            className="border-purple-500 text-purple-600 hover:bg-purple-50"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Générer avec IA
          </Button>
          <Button
            variant="outline"
            onClick={handleGetTrends}
            disabled={aiLoading}
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
            Tendances
          </Button>
          <Button
            variant="outline"
            onClick={handleGetBestTime}
            disabled={aiLoading}
            className="border-green-500 text-green-600 hover:bg-green-50"
          >
            {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
            Meilleurs moments
          </Button>
        </div>
      </div>

      {/* AI Content Generation Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Générer du contenu avec l'IA
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Sujet / Thème *
              </Label>
              <Input
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                placeholder="Ex: Nouveau burger végétarien, Festival de ce weekend..."
                className="mt-1"
              />
            </div>
            
            {/* Context area - NEW */}
            <div className="bg-purple-50 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-purple-500 mt-0.5" />
                <div>
                  <Label className="text-purple-800 font-semibold">Contexte détaillé (optionnel)</Label>
                  <p className="text-sm text-purple-600">Plus vous donnez d'informations, meilleur sera le résultat</p>
                </div>
              </div>
              <Textarea
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
                placeholder="Décrivez en détail ce que vous voulez communiquer :
• Quel est le message principal ?
• Y a-t-il une promotion ou offre spéciale ?
• Quels éléments mettre en avant (ingrédients, prix, lieu...) ?
• Y a-t-il une date limite ou urgence ?
• Quel appel à l'action voulez-vous ?"
                rows={5}
                className="bg-white"
              />
              
              {/* Link to event */}
              <div>
                <Label className="text-sm text-purple-700">Lier à un événement</Label>
                <Select value={aiEventId} onValueChange={setAiEventId}>
                  <SelectTrigger className="mt-1 bg-white">
                    <SelectValue placeholder="Aucun événement (optionnel)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun événement</SelectItem>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.titre} - {event.date_debut && format(parseISO(event.date_debut), "dd/MM/yyyy")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plateforme</Label>
                <Select value={aiPlatform} onValueChange={setAiPlatform}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ton</Label>
                <Select value={aiTone} onValueChange={setAiTone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="decontracte">Décontracté</SelectItem>
                    <SelectItem value="professionnel">Professionnel</SelectItem>
                    <SelectItem value="humoristique">Humoristique</SelectItem>
                    <SelectItem value="inspirant">Inspirant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              onClick={handleGenerateContent} 
              disabled={aiLoading}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {aiLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération en cours...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Générer le contenu</>
              )}
            </Button>

            {aiResult && (
              <div className="space-y-4 mt-4 border-t pt-4">
                <h3 className="font-semibold">Contenu généré :</h3>
                {aiResult.instagram && (
                  <div className="p-3 bg-pink-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium flex items-center gap-1">
                        <Instagram className="w-4 h-4 text-pink-500" /> Instagram
                      </span>
                      <Button size="sm" variant="outline" onClick={() => applyAIContent("instagram", aiResult.instagram)}>
                        Utiliser
                      </Button>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{aiResult.instagram.text}</p>
                    {aiResult.instagram.hashtags && (
                      <p className="text-xs text-pink-600 mt-2">{aiResult.instagram.hashtags.join(" ")}</p>
                    )}
                  </div>
                )}
                {aiResult.facebook && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium flex items-center gap-1">
                        <Facebook className="w-4 h-4 text-blue-600" /> Facebook
                      </span>
                      <Button size="sm" variant="outline" onClick={() => applyAIContent("facebook", aiResult.facebook)}>
                        Utiliser
                      </Button>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{aiResult.facebook.text}</p>
                    {aiResult.facebook.hashtags && (
                      <p className="text-xs text-blue-600 mt-2">{aiResult.facebook.hashtags.join(" ")}</p>
                    )}
                  </div>
                )}
                {aiResult.tiktok && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium flex items-center gap-1">
                        <TikTokIcon className="w-4 h-4" /> TikTok
                      </span>
                      <Button size="sm" variant="outline" onClick={() => applyAIContent("tiktok", aiResult.tiktok)}>
                        Utiliser
                      </Button>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{aiResult.tiktok.text}</p>
                    {aiResult.tiktok.hashtags && (
                      <p className="text-xs text-gray-600 mt-2">{aiResult.tiktok.hashtags.join(" ")}</p>
                    )}
                  </div>
                )}
                {aiResult.raw && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm whitespace-pre-wrap">{aiResult.raw}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Trends Dialog */}
      <Dialog open={showTrendsDialog} onOpenChange={setShowTrendsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Tendances actuelles
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {trendsData?.current_trends && (
              <div>
                <h3 className="font-semibold mb-2">Tendances du moment</h3>
                <div className="space-y-2">
                  {trendsData.current_trends.map((trend, i) => (
                    <div key={i} className="p-3 bg-blue-50 rounded-lg">
                      <div className="font-medium">{trend.trend}</div>
                      <div className="text-sm text-muted-foreground">Plateforme: {trend.platform}</div>
                      <div className="text-sm mt-1">{trend.how_to_use}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {trendsData?.content_ideas && (
              <div>
                <h3 className="font-semibold mb-2">Idées de contenu</h3>
                <ul className="list-disc list-inside space-y-1">
                  {trendsData.content_ideas.map((idea, i) => (
                    <li key={i} className="text-sm">{idea}</li>
                  ))}
                </ul>
              </div>
            )}
            {trendsData?.hashtag_trends && (
              <div>
                <h3 className="font-semibold mb-2">Hashtags tendance</h3>
                <div className="flex flex-wrap gap-2">
                  {trendsData.hashtag_trends.map((tag, i) => (
                    <Badge key={i} variant="secondary">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
            {trendsData?.tips && (
              <div className="p-3 bg-yellow-50 rounded-lg">
                <h3 className="font-semibold mb-1">Conseils</h3>
                <p className="text-sm">{trendsData.tips}</p>
              </div>
            )}
            {trendsData?.raw && (
              <div className="p-3 bg-gray-50 rounded-lg whitespace-pre-wrap text-sm">
                {trendsData.raw}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Best Time Dialog */}
      <Dialog open={showBestTimeDialog} onOpenChange={setShowBestTimeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-500" />
              Meilleurs moments pour publier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {bestTimeData?.instagram && (
              <div className="p-3 bg-pink-50 rounded-lg">
                <h3 className="font-semibold flex items-center gap-2 mb-2">
                  <Instagram className="w-4 h-4 text-pink-500" /> Instagram
                </h3>
                <div className="text-sm">
                  <p><strong>Jours :</strong> {bestTimeData.instagram.best_days?.join(", ")}</p>
                  <p><strong>Heures :</strong> {bestTimeData.instagram.best_hours?.join(", ")}</p>
                  <p className="text-muted-foreground mt-1">{bestTimeData.instagram.explanation}</p>
                </div>
              </div>
            )}
            {bestTimeData?.facebook && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <h3 className="font-semibold flex items-center gap-2 mb-2">
                  <Facebook className="w-4 h-4 text-blue-600" /> Facebook
                </h3>
                <div className="text-sm">
                  <p><strong>Jours :</strong> {bestTimeData.facebook.best_days?.join(", ")}</p>
                  <p><strong>Heures :</strong> {bestTimeData.facebook.best_hours?.join(", ")}</p>
                  <p className="text-muted-foreground mt-1">{bestTimeData.facebook.explanation}</p>
                </div>
              </div>
            )}
            {bestTimeData?.tiktok && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <h3 className="font-semibold flex items-center gap-2 mb-2">
                  <TikTokIcon className="w-4 h-4" /> TikTok
                </h3>
                <div className="text-sm">
                  <p><strong>Jours :</strong> {bestTimeData.tiktok.best_days?.join(", ")}</p>
                  <p><strong>Heures :</strong> {bestTimeData.tiktok.best_hours?.join(", ")}</p>
                  <p className="text-muted-foreground mt-1">{bestTimeData.tiktok.explanation}</p>
                </div>
              </div>
            )}
            {bestTimeData?.general_tips && (
              <div className="p-3 bg-yellow-50 rounded-lg">
                <h3 className="font-semibold mb-1">Conseils généraux</h3>
                <p className="text-sm">{bestTimeData.general_tips}</p>
              </div>
            )}
            {bestTimeData?.raw && (
              <div className="p-3 bg-gray-50 rounded-lg whitespace-pre-wrap text-sm">
                {bestTimeData.raw}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="calendar" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Calendrier
          </TabsTrigger>
          <TabsTrigger value="gallery" className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            Galerie
          </TabsTrigger>
        </TabsList>

        {/* CALENDRIER TAB */}
        <TabsContent value="calendar" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Calendrier des Publications
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
                >
                  ← Précédent
                </Button>
                <h3 className="text-lg font-semibold capitalize">
                  {format(currentMonth, "MMMM yyyy", { locale: fr })}
                </h3>
                <Button
                  variant="outline"
                  onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
                >
                  Suivant →
                </Button>
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-muted-foreground p-2">
                    {day}
                  </div>
                ))}
                {daysInMonth.map((day, index) => {
                  const dayPosts = getPostsForDay(day);
                  const isToday = isSameDay(day, new Date());
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-24 p-1 border rounded cursor-pointer hover:bg-gray-50 ${
                        isToday ? "bg-primary/10 border-primary" : ""
                      } ${!isSameMonth(day, currentMonth) ? "opacity-50" : ""}`}
                      onClick={() => {
                        setSelectedDate(day);
                        setPostForm(prev => ({
                          ...prev,
                          scheduled_date: format(day, "yyyy-MM-dd")
                        }));
                        setShowPostDialog(true);
                      }}
                    >
                      <div className="text-sm font-medium mb-1">
                        {format(day, "d")}
                      </div>
                      <div className="space-y-1">
                        {dayPosts.slice(0, 2).map(post => (
                          <div
                            key={post.id}
                            className={`text-xs p-1 rounded truncate text-white ${statusColors[post.status]}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditPost(post);
                            }}
                          >
                            {post.title}
                          </div>
                        ))}
                        {dayPosts.length > 2 && (
                          <div className="text-xs text-muted-foreground">
                            +{dayPosts.length - 2} autres
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Posts list */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">Publications à venir</h3>
                <div className="space-y-3">
                  {posts
                    .filter(p => p.status === "scheduled" || p.status === "draft")
                    .slice(0, 10)
                    .map(post => (
                      <Card
                        key={post.id}
                        className="border hover:shadow-md transition-shadow"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1">
                              <Badge className={`${statusColors[post.status]} mt-1`}>
                                {statusLabels[post.status]}
                              </Badge>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-secondary">{post.title}</div>
                                <div className="text-sm text-muted-foreground mb-2">
                                  {format(parseISO(post.scheduled_date), "dd/MM/yyyy", { locale: fr })}
                                  {post.scheduled_time && ` à ${post.scheduled_time}`}
                                </div>
                                
                                {/* Inline content editing */}
                                {editingInlineId === post.id ? (
                                  <div className="space-y-2">
                                    <Textarea
                                      value={inlineContent}
                                      onChange={(e) => setInlineContent(e.target.value)}
                                      rows={4}
                                      className="w-full text-sm"
                                      placeholder="Contenu de la publication..."
                                      autoFocus
                                    />
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => saveInlineEdit(post.id)}
                                        disabled={savingInline}
                                        className="bg-green-600 hover:bg-green-700"
                                      >
                                        {savingInline ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <><Save className="w-4 h-4 mr-1" /> Sauvegarder</>
                                        )}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={cancelInlineEdit}
                                      >
                                        <XCircle className="w-4 h-4 mr-1" /> Annuler
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div 
                                    className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors group"
                                    onClick={() => startInlineEdit(post)}
                                  >
                                    {post.content ? (
                                      <p className="whitespace-pre-wrap">{post.content.substring(0, 200)}{post.content.length > 200 ? "..." : ""}</p>
                                    ) : (
                                      <p className="text-muted-foreground italic">Cliquez pour ajouter du contenu...</p>
                                    )}
                                    <div className="flex items-center gap-1 text-xs text-purple-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Edit className="w-3 h-3" />
                                      Cliquer pour modifier
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className="flex gap-1">
                                {post.platforms?.includes("instagram") && (
                                  <Instagram className="w-4 h-4 text-pink-500" />
                                )}
                                {post.platforms?.includes("facebook") && (
                                  <Facebook className="w-4 h-4 text-blue-600" />
                                )}
                                {post.platforms?.includes("tiktok") && (
                                  <TikTokIcon className="w-4 h-4" />
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditPost(post)}
                                title="Modifier tout"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePost(post.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Media preview */}
                          {post.media && post.media.length > 0 && (
                            <div className="mt-3 flex gap-2 overflow-x-auto">
                              {post.media.slice(0, 4).map(m => (
                                <img
                                  key={m.id}
                                  src={m.thumbnail_url || m.url}
                                  alt=""
                                  className="w-16 h-16 rounded object-cover flex-shrink-0"
                                />
                              ))}
                              {post.media.length > 4 && (
                                <div className="w-16 h-16 rounded bg-gray-200 flex items-center justify-center flex-shrink-0 text-sm text-gray-600">
                                  +{post.media.length - 4}
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GALERIE TAB */}
        <TabsContent value="gallery" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-primary" />
                Galerie Photos & Vidéos
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={mediaFilter} onValueChange={setMediaFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="photo">Photos</SelectItem>
                    <SelectItem value="video">Vidéos</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Visibilité" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="private">Privé</SelectItem>
                    <SelectItem value="public">Public</SelectItem>
                  </SelectContent>
                </Select>
                <label className="cursor-pointer">
                  <Button as="span" className="bg-primary hover:bg-primary/90" disabled={uploadingMedia}>
                    {uploadingMedia ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Ajouter
                  </Button>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleUploadMedia}
                    className="hidden"
                  />
                </label>
              </div>
            </CardHeader>
            <CardContent>
              {filteredMedia.length === 0 ? (
                <div className="text-center py-12">
                  <Image className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun média</p>
                  <p className="text-sm text-muted-foreground">
                    Uploadez vos photos et vidéos pour les utiliser dans vos publications
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {filteredMedia.map(media => (
                    <div
                      key={media.id}
                      className="relative group rounded-lg overflow-hidden border"
                    >
                      {media.media_type === "photo" ? (
                        <img
                          src={media.url}
                          alt={media.description || ""}
                          className="w-full h-40 object-cover"
                        />
                      ) : (
                        <div className="w-full h-40 bg-gray-200 flex items-center justify-center">
                          <Video className="w-12 h-12 text-gray-500" />
                        </div>
                      )}
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleUpdateMediaVisibility(
                            media.id,
                            media.visibility === "public" ? "private" : "public"
                          )}
                        >
                          {media.visibility === "public" ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteMedia(media.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Visibility badge */}
                      <div className="absolute top-2 right-2">
                        <Badge
                          className={media.visibility === "public" ? "bg-green-500" : "bg-gray-500"}
                        >
                          {media.visibility === "public" ? (
                            <Eye className="w-3 h-3" />
                          ) : (
                            <EyeOff className="w-3 h-3" />
                          )}
                        </Badge>
                      </div>
                      
                      {/* Type badge */}
                      <div className="absolute top-2 left-2">
                        <Badge variant="secondary">
                          {media.media_type === "photo" ? (
                            <Image className="w-3 h-3" />
                          ) : (
                            <Video className="w-3 h-3" />
                          )}
                        </Badge>
                      </div>
                      
                      {/* Event info */}
                      {media.event_title && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 truncate">
                          {media.event_title}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
